import { UserRole } from '@/shared/enums/user-role.enum';

/**
 * Every internal Carofi staff role — the whole `UserRole` enum minus `guest` and the
 * partner / merchant / referral roles, who sign in to the partner portal instead.
 *
 * Use this for surfaces that belong to all employees rather than to one department:
 * the Dashboard and the HR / Leave pages. Those had each grown their own hand-written
 * copy of "all internal roles", and every copy had drifted — the insurance and helpdesk
 * roles were missing from all of them, which is what locked insurance staff out of the
 * Dashboard. One list means the next role added to the enum only has to land here.
 */
/** Any role that may use the internal admin app (excludes guest). */
export const ALL_AUTHENTICATED_ROLES: UserRole[] = Object.values(UserRole).filter(
  (role): role is UserRole => role !== UserRole.GUEST
);

export const ADMIN_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.SALES_AGENT,
  UserRole.SALES_MANAGER,
  UserRole.SALES_QUALITY_MANAGER,
  UserRole.SALES_QUALITY_AGENT,
  UserRole.MARKETING_EXECUTIVE,
  UserRole.FINANCE_OFFICER,
  UserRole.E_COMMERCE_AUTO,
  UserRole.E_COMMERCE_FINANCE,
  UserRole.E_COMMERCE_SERVICE_CENTER,
  UserRole.E_COMMERCE_REFERRAL,
  UserRole.E_COMMERCE_MANAGER,
  UserRole.INSURANCE_MANAGER,
  UserRole.INSURANCE_AGENT,
  UserRole.OPERATION_MANAGER,
  UserRole.OPERATION_AGENT,
  UserRole.HELP_DESK_SUPPORT,
  UserRole.HR_REPRESENTATIVE,
  UserRole.HR_MANAGER
];

export const TEAM_DATA_ROLES = new Set<UserRole>([
  UserRole.ADMIN,
  UserRole.SALES_AGENT,
  UserRole.SALES_MANAGER,
  UserRole.SALES_QUALITY_MANAGER,
  UserRole.SALES_QUALITY_AGENT,
  UserRole.E_COMMERCE_AUTO,
  UserRole.E_COMMERCE_FINANCE,
  UserRole.E_COMMERCE_SERVICE_CENTER,
  UserRole.E_COMMERCE_REFERRAL,
  UserRole.E_COMMERCE_MANAGER,
  UserRole.INSURANCE_MANAGER,
  UserRole.INSURANCE_AGENT,
  UserRole.HR_REPRESENTATIVE,
  UserRole.HR_MANAGER
]);

const AccessRoles = {
  applications: {
    add: [
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.SALES_QUALITY_MANAGER,
      UserRole.SALES_QUALITY_AGENT,
      UserRole.SALES_AGENT,
      UserRole.FINANCE_OFFICER,
      UserRole.OPERATION_MANAGER,
      UserRole.E_COMMERCE_FINANCE,
      UserRole.INSURANCE_MANAGER,
      UserRole.INSURANCE_AGENT
    ],
    /**
     * Roles that may edit any application outright.
     *
     * The eCommerce and insurance roles are deliberately absent: their edit
     * right is per-application now, decided by the customer server from the
     * assignment and the reporting tree, and arrives as
     * `workflowCapabilities.canEditApplication` on the application itself.
     * Listing them here as well would show an Edit button the server refuses.
     */
    edit: [
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.SALES_QUALITY_MANAGER,
      UserRole.SALES_QUALITY_AGENT,
      UserRole.FINANCE_OFFICER,
      UserRole.OPERATION_MANAGER
    ],
    /**
     * Approve, reject and requires-updates. A credit decision, and the only
     * gate on it — there is no second path that lets anyone else approve.
     */
    takeFinancialActions: [UserRole.ADMIN, UserRole.FINANCE_OFFICER, UserRole.OPERATION_MANAGER]
  },
  transactions: {
    walletCreditDebit: [UserRole.ADMIN, UserRole.FINANCE_OFFICER]
  },
  leads: {
    /**
     * Who may assign or reassign a lead. The insurance roles are included on purpose:
     * when a lead's contact type changes, or a second product needs a different
     * specialist on the same customer, they hand the lead over themselves.
     */
    assignActions: [
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.SALES_QUALITY_MANAGER,
      UserRole.SALES_QUALITY_AGENT,
      UserRole.E_COMMERCE_AUTO,
      UserRole.E_COMMERCE_MANAGER,
      UserRole.INSURANCE_MANAGER,
      UserRole.INSURANCE_AGENT
    ],
    editActions: [
      UserRole.ADMIN,
      UserRole.SALES_AGENT,
      UserRole.SALES_MANAGER,
      UserRole.SALES_QUALITY_MANAGER,
      UserRole.SALES_QUALITY_AGENT,
      UserRole.E_COMMERCE_AUTO,
      UserRole.E_COMMERCE_MANAGER,
      UserRole.INSURANCE_MANAGER,
      UserRole.INSURANCE_AGENT
    ],
    bulkStatusActions: [
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.SALES_QUALITY_MANAGER,
      UserRole.SALES_QUALITY_AGENT
    ]
  }
};

export default AccessRoles;
